import React, { useEffect, useState } from 'react';
import PageToolBar, {ToolDivider} from '@gakki/components/PageToolBar';
import cx from 'classnames';
import Layout from '@gakki/components/Layout';
import usePageData from '@common/hooks/usePageData';
import {StepStatus, useAppContext} from '@gakki/context/app-context';
import config from '@gakki/step-config';
import ShiftScheduleSettingForm from './components/shift-schedule-setting-form';
import {ScheduleSettingBody, ShiftSchedule, Shift} from '@gakki/api/types';
import {useAuthContext, useClient} from '@gakki/context/auth-context';
import {useConfirmMutate, useSaveMutate} from '@gakki/api/shift-setting';
import {
  ResetDialog,
  ResetSuccessDialog,
  FailDialog,
} from '@gakki/components/Dialog';
import {Button} from '@mayo/mayo-ui-gakki';
import {ReactComponent as DownloadIcon} from 'app/gakki/assets/download.svg';
import {ReactComponent as UploadIcon} from 'app/gakki/assets/upload.svg';
import {ReactComponent as RedoIcon} from 'app/gakki/assets/redo.svg';
import {useXlsExport} from 'hooks/useXlsExport';
import {usePost} from 'hooks/usePost';
import {toast} from 'react-toastify';
import {isEmpty, clone, isNil} from 'ramda';
import {timeToDateTime} from '@gakki/utils/time';
import {Interval} from 'luxon';
import {useMutation} from 'react-query';
import {useCompanyContext} from '@gakki/context/company-context';
import { resolve } from 'path';

const isNullArray = (array: null[]) => array.every(i => i === null);

const isNilOrEmpty = (v: any) => {
  return isNil(v) || isEmpty(v);
}

const deleteEmptyKey = (obj: any) => {
  const newObj = clone(obj);
  Object.entries(newObj).forEach(([key, value]) => {
    if (!value) {
      delete newObj[key];
    }
  });
  return newObj;
};

function ShiftScheduleSetting() {
  const client = useClient();
  const {companyId} = useAuthContext();
  const {
    data,
    init,
    edit,
    status,
    remove,
    updatedTime,
  } = usePageData<ScheduleSettingBody>('shift-schedule', {
    shiftSchedules: [],
    shifts: [],
    employeeSchedules: [],
  });
  const [errorMessage, setErrorMessage] = useState<any>(null);
  const [showUploadFailDialog, setShowUploadFailDialog] = useState(false);
  const [showFailDialog, setShowFailDialog] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [showResetSuccessDialog, setShowResetSuccessDialog] = useState(false);
  const {setData: exportExcelData} = useXlsExport();
  const { mutate } = usePost(`attendance/company/${companyId}/schedule/import/excel`, {
    onSuccess: async (d: any) => {
      if (d.data) {
        setShiftAndShiftScheduleError(d.data, true)
        toast.success('班次及班別已匯入，請確認資料是否正確，並接續【步驟3. 排班員工設定】。', {
          hideProgressBar: true,
          position: 'top-center',
        });
      }
    },
    onError: () => {
      setErrorMessage('匯入範本格式有誤，請重新下載範本');
      setShowUploadFailDialog(true);
    },
    onMutate: () => setIsLoading(true),
    onSettled: () => setIsLoading(false),
    hiddenErrorMessage: true,
  });
  useEffect(() => {
    if (data === null || status === StepStatus.init) {
      setIsLoading(true);
      client.get(`attendance/company/${companyId}/schedule/setting`).then(d => {
        setShiftAndShiftScheduleError(d.data)
        setIsLoading(false);
      });
      return;
    }
  }, []);
  const {
    changeStep,
    step,
    setIsLoading,
    isLoading,
    save,
    saveAndGoStep,
    saveFail,
    confirm,
    refetchEnablePY,
    onPrev,
  } = useAppContext();
  const saveData = (data: ScheduleSettingBody) => {
    return client.put(`attendance/company/${companyId}/schedule/setting`, {
      data,
    });
  }
  
  const confirmData = () => {
    return client.put(`attendance/company/${companyId}/schedule/setting/confirm`);
  }
  
  const {mutateAsync: saveToServer} = useSaveMutate({
    onSuccess: () => save(),
    onError: () => saveFail(),
    onMutate: () => setIsLoading(true),
    onSettled: () => setIsLoading(false),
  });

  const {mutateAsync: handleConfirm} = useMutation(
    (data: ScheduleSettingBody) => saveData(data)
    .then(() => refetchEnablePY('next', step + 1))
    .then(async (company) => {
      try {
        if (company.enablePY) {
          await confirmData();
          setIsLoading(false)
          changeStep(step + 1);
        } else {
          setIsLoading(false)
          changeStep(11);
        }
        confirm();
        remove();
      } catch (e: any) {
        throw new Error(e);
      }
    }),
    {
      onMutate: () => setIsLoading(true),
      onError: (error: {code: number; message: string}) => {
        setIsLoading(false)
        if (error.code !== 500) {
          toast.error(error.message || '儲存失敗', {
            hideProgressBar: true,
            position: 'top-center',
            autoClose: false
          });
        }
      },
    },
  );

  useEffect(() => {
    if (typeof saveAndGoStep === 'number') {
      saveToServer(data)
    }
  }, [saveAndGoStep])

  const checkShiftCycleTypeNotMatch = () => {
    // 班次與班別重複設定需即時驗證，故與匯入驗證分開
    return data.shifts?.some((item) => {
      return item.shiftSchedules?.some(shiftSchedule =>
        data.shiftSchedules.find(s => s.id === shiftSchedule.shiftScheduleId)?.cycleType !== item.cycleType)
    })
  }

  const validationShift = (shifts: Shift[]) => {
    return shifts.map(shift => {
      const unselectedShiftSchedules = shift.shiftSchedules && shift.shiftSchedules.length === 0;

      // 後端使用 -1 取代 null
      const error = deleteEmptyKey({
        baseLine: isNilOrEmpty(shift.baseLine) ? '必填' : null,
        cycleType: isNilOrEmpty(shift.cycleType) || Number(shift.cycleType) < 0 ? '必填' : null,
        shiftScheduleCalendarType: isNilOrEmpty(shift.shiftScheduleCalendarType) || Number(shift.shiftScheduleCalendarType) < 0 ? '必填' : null,
        isAutoGenerateCalendar: isNilOrEmpty(shift.isAutoGenerateCalendar) ? '必填' : null,
        defaultShiftSchedule: isNilOrEmpty(shift.defaultShiftSchedule) ? '必填' : null,
        shiftSchedules: (unselectedShiftSchedules && '必填') || null,
      })

      return { ...shift, error }
    })
  }

  const validationShiftSchedule = (shiftSchedules: ShiftSchedule[]) => {
    return shiftSchedules.map(shiftSchedule => {
      const isRepeatCycle = shiftSchedule.cycleType === 0;
      let isWorkTimesContainsDayStartTime = false
      const workStart = isNilOrEmpty(shiftSchedule.startWorkingTime)
      ? undefined
      : timeToDateTime(shiftSchedule.startWorkingTime);
      const workEnd = isNilOrEmpty(shiftSchedule.offWorkingTime)
      ? undefined
      : timeToDateTime(shiftSchedule.offWorkingTime, workStart);
      const baseLine = isNilOrEmpty(shiftSchedule.dayStartTime)
      ? undefined
      : timeToDateTime(shiftSchedule.dayStartTime, workStart);

      if (workStart && workEnd && baseLine) {
        isWorkTimesContainsDayStartTime = Interval.fromDateTimes(workStart, workEnd).contains(baseLine)
      }

      let hasRestTimes = shiftSchedule.restTimes instanceof Array && shiftSchedule.restTimes.length > 0
      let isRestTimesOverlapped = false
      if (hasRestTimes) {
        const restTimes = shiftSchedule.restTimes
        .filter((t) => !isNilOrEmpty(t.startTime) && !isNilOrEmpty(t.endTime))
        .map(t => {
          const start = timeToDateTime(t.startTime);
          const end = timeToDateTime(t.endTime, start);
          return Interval.fromDateTimes(start, end);
        })
        restTimes.forEach((current, i) => {
          if (restTimes.some((t, idx) => i !== idx && (
            (t.contains(current.start) && t.end.toMillis() > current.start.toMillis()) ||
            (t.contains(current.end) && t.start.toMillis() < current.end.toMillis())
          ))) {
            isRestTimesOverlapped = true;
            return;
          }
        })
      }

      const restTimesError = (shiftSchedule?.restTimes || []).map(restTime => {
        const restStartDate = isNilOrEmpty(restTime?.startTime)
        ? undefined
        : timeToDateTime(restTime?.startTime, workStart);
        const restEndDate = isNilOrEmpty(restTime?.endTime)
        ? undefined
        : timeToDateTime(restTime?.endTime, workStart);

        let isWorkTimesNotContainsRestStart = false
        if (workStart && workEnd && restTime?.startTime) {
          const startDateTime = timeToDateTime(restTime?.startTime, workStart);
          isWorkTimesNotContainsRestStart = !Interval.fromDateTimes(workStart, workEnd).contains(startDateTime)
        }

        let isWorkTimesNotContainsRestEnd = false
        if (workStart && workEnd && restTime?.endTime) {
          const endDateTime = timeToDateTime(restTime?.endTime, workStart);
          isWorkTimesNotContainsRestEnd = !Interval.fromDateTimes(workStart, workEnd).contains(endDateTime)
        }

        let isRestEndBeforeRestStart = false
        if (workStart && restStartDate && restEndDate) {
          isRestEndBeforeRestStart = Interval.fromDateTimes(workStart, restStartDate).contains(restEndDate)
        }

        const restTimeError = deleteEmptyKey({
          startTime: (isNilOrEmpty(restTime?.startTime) && '必填') ||
          (isWorkTimesNotContainsRestStart && '休息開始時間區需介於上班時間區間') ||
          (isRestEndBeforeRestStart && '休息結束時間不能早於休息開始時間') ||
          null,
          endTime: (isNilOrEmpty(restTime?.endTime) && '必填') ||
          (isWorkTimesNotContainsRestEnd && '休息結束時間區需介於上班時間區間') ||
          (isRestEndBeforeRestStart && '休息結束時間不能早於休息開始時間') ||
          null,
        })
        return isNilOrEmpty(restTimeError) ? null : restTimeError;
      })
      // 後端使用 -1 取代 null
      const cyclesError = isRepeatCycle ? deleteEmptyKey({
        sunday: isNilOrEmpty(shiftSchedule.cycles?.sunday) || Number(shiftSchedule.cycles?.sunday) < 0 ? '必填' : null,
        monday: isNilOrEmpty(shiftSchedule.cycles?.monday) || Number(shiftSchedule.cycles?.monday) < 0 ? '必填' : null,
        tuesday: isNilOrEmpty(shiftSchedule.cycles?.tuesday) || Number(shiftSchedule.cycles?.tuesday) < 0 ? '必填' : null,
        wednesday: isNilOrEmpty(shiftSchedule.cycles?.wednesday) || Number(shiftSchedule.cycles?.wednesday) < 0 ? '必填' : null,
        thursday: isNilOrEmpty(shiftSchedule.cycles?.thursday) || Number(shiftSchedule.cycles?.thursday) < 0 ? '必填' : null,
        friday: isNilOrEmpty(shiftSchedule.cycles?.friday) || Number(shiftSchedule.cycles?.friday) < 0 ? '必填' : null,
        saturday: isNilOrEmpty(shiftSchedule.cycles?.saturday) || Number(shiftSchedule.cycles?.saturday) < 0 ? '必填' : null,
      }) : null

      const error = deleteEmptyKey({
        isRestTimesNotSet: !hasRestTimes ? '至少設定一組休息時間' : null,
        isRestTimesOverlapped: isRestTimesOverlapped ? '休息時間不可重疊' : null,
        startWorkingTime: isNilOrEmpty(shiftSchedule.startWorkingTime) ? '必填' : null,
        offWorkingTime: isNilOrEmpty(shiftSchedule.offWorkingTime) ? '必填' : null,
        dayStartTime: (isNilOrEmpty(shiftSchedule.dayStartTime) && '必填') ||
        (isWorkTimesContainsDayStartTime && '上班時間區間不可超過日切換時間'),
        restTimes: isNullArray(restTimesError) ? null : restTimesError,
        color: isNilOrEmpty(shiftSchedule.color) ? '必填' : null,
        cycleType: isNilOrEmpty(shiftSchedule.cycleType) || Number(shiftSchedule.cycleType) < 0 ? '必填' : null,
        cycles: isNilOrEmpty(cyclesError) ? null : cyclesError,
      })
      
      return { ...shiftSchedule, error }
    })
  }

  // 匯入驗證只在 進入頁面時 & 匯入檔案後 驗證
  const setShiftAndShiftScheduleError = (source: ScheduleSettingBody = data, isImport?: boolean) => {
    const shiftsWithErrors = validationShift(source.shifts)
    const shiftSchedulesWithErrors = validationShiftSchedule(source.shiftSchedules)
    if (isImport) {
      edit({
        ...source,
        shifts: shiftsWithErrors,
        shiftSchedules: shiftSchedulesWithErrors,
      })
    } else {
      init({
        ...source,
        shifts: shiftsWithErrors,
        shiftSchedules: shiftSchedulesWithErrors,
      })
    }
  }
  const checkShiftAndShiftScheduleInvalid = () => {
    const hasShiftInvalid = data.shifts.some(s => Object.keys(s?.error || {}).length > 0)
    const hasShiftScheduleInvalid = data.shiftSchedules.some(s => Object.keys(s?.error || {}).length > 0)
    return hasShiftInvalid || hasShiftScheduleInvalid
  }

  const downloadExcelSample = async () => {
    const downloadUrl = await client.get('attendance/schedule/sample/excel');
    if (downloadUrl) {
      const response = await fetch(downloadUrl, { method: 'get', cache: "no-cache" })
      const blob = await response.blob();
      exportExcelData({blob, name: `導入精靈排班範本_班次及班別.xlsx`});
    }
  }
  const handleReset = () => {
    setIsLoading(true);
    setShowResetDialog(false);
    client.post(`attendance/company/${companyId}/schedule/reset`).then(d => {
      edit(d.data);
      setIsLoading(false);
      setShowResetSuccessDialog(true);
    });
  }

  const uploadExcelSample = async (event: React.ChangeEvent<any>) => {
    const fileName = (event?.currentTarget?.files[0].name || '').split('.')
    const fileType = fileName[fileName.length - 1]
    if (['xlsx', 'xls'].includes(fileType)) {
      mutate({ file: event?.currentTarget?.files[0] });
    } else {
      setErrorMessage('檔案格式限制XLSX、XLS');
      setShowUploadFailDialog(true);
    }
    event.target.value = null;
  }

  return (
    <React.Fragment>
      <Layout.MainContent>
        <PageToolBar pageTitle={config.attendance.steps.shiftSchedule.name}>
          <Button
            variant='simple'
            colorSchema='primary'
            onClick={() => setShowResetDialog(true)}
            className={'reset-btn'}
            icon={<RedoIcon />}
          >
            全部重設
          </Button>
          <ToolDivider />
          <Button
            variant='simple'
            colorSchema='primary'
            onClick={downloadExcelSample}
            aria-label="download-excel-sample"
            icon={<DownloadIcon />}
          >
            Excel範本下載
          </Button>
          <ToolDivider />
          <label
            className={cx(
            'text-[13px] bg-transparent px-[6px] py-[1px] m-0 leading-none border-0 outline-none',
            'flex items-center justify-center gap-[3px]',
            'inherit hover:text-brand cursor-pointer'
            )}
            aria-label="upload-excel-sample"
          >
            <input id="upload_img" 
              className='hidden'
              type="file"
              accept=".xlsx,.xls"
              onChange={uploadExcelSample}
            />
            <UploadIcon />
            批次上傳  
          </label>  
        </PageToolBar>
        <ShiftScheduleSettingForm
          data={data}
          edit={edit}
          init={init}
        />
      </Layout.MainContent>
      <Layout.Footer
        updatedTime={updatedTime}
        showNextButton
        showPrevButton
        showSaveButton
        showUpdatedTime
        disabledSaveButton={status !== StepStatus.edit || isLoading}
        disabledPrevButton={status === StepStatus.edit || isLoading}
        disabledNextButton={isLoading}
        onSave={async () => {
          try {
            await saveToServer(data);
          } catch (e) {}
        }}
        onNext={async () => {
          if (checkShiftCycleTypeNotMatch() || checkShiftAndShiftScheduleInvalid()) {
            setShowFailDialog(true)
          } else {
            await handleConfirm(data);
          }
        }}
        onPrev={async () => {
          await onPrev();
          remove();
        }}
      />
      <ResetDialog
        text={
          <span>
            全部重置後，
            <br />
            既有排班設定將全數被清空，
            <br />
            請問您依舊要全部重置嗎？
          </span>
        }
        isOpen={showResetDialog}
        onCancel={() => setShowResetDialog(false)}
        onConfirm={handleReset}
      />
      <ResetSuccessDialog
        isOpen={showResetSuccessDialog}
        onCancel={() => setShowResetSuccessDialog(false)}
      />
      <FailDialog
        text={errorMessage}
        isOpen={showUploadFailDialog}
        onConfirm={() => setShowUploadFailDialog(false)}
        onCancel={() => setShowUploadFailDialog(false)}
      />
      <FailDialog
        isOpen={showFailDialog}
        onConfirm={() => setShowFailDialog(false)}
        onCancel={() => setShowFailDialog(false)}
      />
    </React.Fragment>
  );
}

export default ShiftScheduleSetting;
